import { MapboxBasic } from 'components/MapBox/Basic';
import { OrderFilterOptions } from 'const/OrderFilterOptions';
import { Dayjs } from 'dayjs';
import { FeatureCollection } from 'geojson';
import { PlannerData } from 'hooks/usePlannerData';
import React, {
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CalendarInputComponent } from 'ReusableComponents/CalendarInput/CalendarInputComponent';
import { BundleJson, BundleStatus } from 'types/bundleTypes';
import { ProductType, Region } from 'types/orderTypes';
import { useOrderWithHubScan } from 'utils/OrderFilter/WithHubScan';
import { usePlannerFilteredData } from './hooks/usePlannerFilteredData';
import { GeoOrders, ordersInsidePolygon } from './map/utils/mapUtils';
import { FilterType } from './Planner';
import { PlannerToolsBundle } from './tools/PlannerToolsBundle';
import { PlannerToolsFilter } from './tools/PlannerToolsFilter';
import { PlannerToolsSelectedOrders } from './tools/PlannerToolsSelectedOrders';
import { PlannerBundle, PlannerOrder } from './utils/usePlannerData';

const initFilters: FilterType = {
  windows: [],
  stores: [],
  bundleStatus: [BundleStatus.Draft],
  productType: ProductType.delivery,
  [OrderFilterOptions.WITH_TEMP_CONTROL]: false,
  [OrderFilterOptions.WITHOUT_TEMP_CONTROL]: false,
  [OrderFilterOptions.XXL]: false,
  [OrderFilterOptions.WITH_PARTNER_SCAN]: false,
  [OrderFilterOptions.WITHOUT_PARTNER_SCAN]: false,
  [OrderFilterOptions.WITH_HUB_SCAN]: false,
  [OrderFilterOptions.WITHOUT_HUB_SCAN]: false,
  [OrderFilterOptions.WITH_ID_CHECK]: false,
  [OrderFilterOptions.RESCHEDULED]: false,
};

interface PlannerContainerProps {
  activeDate: Dayjs;
  region: Region;
  plannerData: PlannerData;
  onDateChange: (date: string) => void;
}

export const PlannerContainer = React.forwardRef(
  (
    {
      activeDate,
      region,
      plannerData,
      onDateChange,
      ...props
    }: PlannerContainerProps,
    ref: MutableRefObject<HTMLDivElement> | ((instance: HTMLDivElement) => void)
  ) => {
    const [selectedOrders, setSelectedOrders] = useState<PlannerOrder[]>([]);
    const [filters, setFilters] = useState<FilterType>(initFilters);
    const [shouldClearPolygon, setShouldClearPolygon] =
      useState<boolean>(false);
    const hasPolygon = useRef(false);

    /* Always get Order IDs based on selectedOrders */
    const selectedOrderIds = useMemo(
      () => selectedOrders.map(({ id }) => id),
      [selectedOrders]
    );

    const scans = useOrderWithHubScan(plannerData.fetchedOrders);
    const selectedOrdersRef = useRef<PlannerOrder[]>([]);

    const updateSelectedOrders = (newSelectedOrder: PlannerOrder[]) => {
      setSelectedOrders(newSelectedOrder);
      selectedOrdersRef.current = newSelectedOrder;
    };

    const plannerFilteredData = usePlannerFilteredData(
      filters,
      plannerData.fetchedBundles,
      plannerData.fetchedOrders,
      scans
    );

    /* All data that have Filtered Data  */
    const filteredWindowGroups =
      plannerFilteredData?.windowGroups || plannerData.windowGroups;
    const filteredOrders =
      plannerFilteredData?.orders || plannerData.ordersFeature;
    const filteredMultiMarkers =
      plannerFilteredData?.multiMarkers || plannerData.multiMarkers;
    const filteredBundles =
      plannerFilteredData?.bundles || plannerData.bundleFeature;
    const filteredFetchedBundles =
      plannerFilteredData?.fetchedBundles || plannerData.fetchedBundles;

    const resetPolygonDrawing = () => {
      setShouldClearPolygon(false);
    };

    const clearPolygon = () => {
      setShouldClearPolygon(true);
    };

    const clearSelectedOrders = useCallback(() => {
      updateSelectedOrders([]);
    }, []);

    const handleOrderSelect = (
      clickedOrders: PlannerOrder[],
      isPolygon = false
    ) => {
      if (isPolygon) {
        hasPolygon.current = true;
        updateSelectedOrders(clickedOrders);
      } else {
        const newSelectedOrders = [...selectedOrdersRef.current];

        clickedOrders.forEach(order => {
          const orderIndex = newSelectedOrders.findIndex(
            currentStateOrder => currentStateOrder.id === order.id
          );

          if (orderIndex === -1) {
            newSelectedOrders.push(order);
          } else {
            newSelectedOrders.splice(orderIndex, 1);
          }
        });
        updateSelectedOrders(newSelectedOrders);

        /* It should remove all existing Polygon */
        if (hasPolygon.current) {
          clearPolygon();
          hasPolygon.current = false;
        }
      }
    };

    const onFilterChange = (newFilters: FilterType) => {
      setFilters(newFilters);
    };

    const onFilterReset = () => {
      setFilters(initFilters);
    };

    const updateBundleFromPusher = () => {
      plannerData.update(
        selectedOrders.map(order => order.id),
        'order-assign-to-bundle'
      );
      clearSelectedOrders();
    };

    const onUpdatePolygonArea = (
      polygonData: FeatureCollection,
      orders: GeoOrders
    ) => {
      handleOrderSelect(ordersInsidePolygon(orders, polygonData), true);
    };

    const getBundleByStatus = (
      plannerBundle: (PlannerBundle | BundleJson)[]
    ) => {
      const bundleStatusFilter = filters.bundleStatus;

      return plannerBundle.filter(bundle =>
        bundleStatusFilter.includes(bundle.status)
      );
    };

    const refetchPlannerData = () => {
      clearPolygon();
      clearSelectedOrders();
      plannerData.reFetch();
    };

    const everythingHasBeenLoaded =
      plannerData.done && plannerFilteredData.done;

    if (everythingHasBeenLoaded) {
      return (
        <div
          className="planner"
          style={{
            gridTemplateColumns: '525px 1fr',
          }}
          ref={ref}
        >
          <MapboxBasic
            hasLegend
            hasOrders
            hasMultiMarker
            hasBundle
            hasHubMarker
            hasAreaOverlay
            enableDraw
            fitToAreaOverlayBounds={false}
            region={region}
            windowGroups={filteredWindowGroups}
            orders={filteredOrders}
            selectedOrders={selectedOrders}
            selectedOrderIds={selectedOrderIds}
            multiMarkers={filteredMultiMarkers}
            bundles={filteredBundles}
            fetchedBundles={filteredFetchedBundles}
            shouldClearPolygon={shouldClearPolygon}
            resetPolygonDrawing={resetPolygonDrawing}
            onClickOrderMarker={handleOrderSelect}
            updateBundleFromPusher={updateBundleFromPusher}
            onCreatePolygonDrawing={onUpdatePolygonArea}
            onUpdatePolygonDrawing={onUpdatePolygonArea}
          />

          <div className="tools">
            {!plannerData.loading && (
              <>
                <CalendarInputComponent
                  date={activeDate}
                  setDate={onDateChange}
                />

                <PlannerToolsFilter
                  values={filters}
                  orders={plannerData.ordersFeature}
                  bundles={plannerData.bundleFeature}
                  fetchedOrders={plannerData.fetchedOrders}
                  windowGroups={plannerData.windowGroups}
                  onChange={onFilterChange}
                  onReset={onFilterReset}
                />
              </>
            )}

            <PlannerToolsSelectedOrders
              selectedOrders={selectedOrders}
              clearSelectedOrders={clearSelectedOrders}
              handleOrderSelect={handleOrderSelect}
              bundles={getBundleByStatus(filteredBundles) as PlannerBundle[]}
              reFetch={refetchPlannerData}
            />

            <PlannerToolsBundle
              region={region}
              activeDate={activeDate}
              bundles={
                getBundleByStatus(plannerData.bundleFeature) as PlannerBundle[]
              }
              fetchedBundles={
                getBundleByStatus(plannerData.fetchedBundles) as BundleJson[]
              }
              selectedOrderCount={selectedOrders.length}
              refetching={plannerData.refetching}
              selectedOrders={selectedOrders}
              selectedOrderIds={selectedOrderIds}
              reFetch={refetchPlannerData}
            />
          </div>
        </div>
      );
    }

    return <></>;
  }
);
PlannerContainer.displayName = 'PlannerContainer';
PlannerContainer.whyDidYouRender = true;
